import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Flex, FormControl, FormHelperText, FormLabel, Grid, IconButton, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Spinner, Text, Textarea, useToast,
} from '@chakra-ui/react';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiClock } from 'react-icons/fi';
import {
  MONTHS, MONTHS_SHORT, getPeriods, savePeriod, updateAccountLimits, errorMessage, formatMoney, monthLabel,
} from 'api/administrador/finance';
import { PeriodStatusTag } from './ui';

const toNumber = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

/**
 * Caja inicial del mes. Bancos: se escribe cada mes y cuenta (normalmente del extracto); si no
 * coincide con el cierre calculado se pide el motivo. Flujo de caja: se arrastra sola del cierre
 * anterior; solo se escribe el primer mes, y aquí van los topes de la caja.
 */
export default function OpeningModal({ isOpen, onClose, account, accounts, initialMonth, onSaved, canEdit }) {
  const toast = useToast();
  const [accountId, setAccountId] = useState(account?.id);
  const [year, setYear] = useState(initialMonth?.year);
  const [selected, setSelected] = useState(initialMonth);
  const [periods, setPeriods] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [opening, setOpening] = useState('');
  const [reason, setReason] = useState('');
  const [confirmPast, setConfirmPast] = useState(false);
  const [limits, setLimits] = useState({ min: '', max: '' });
  const [saving, setSaving] = useState(false);

  const current = (accounts || []).find((a) => a.id === accountId) || account;
  const isCaja = current?.book === 'caja_menor';

  useEffect(() => {
    if (!isOpen) return;
    setAccountId(account?.id);
    setSelected(initialMonth);
    setYear(initialMonth?.year);
    setPickerOpen(false);
    setConfirmPast(false);
  }, [isOpen, account, initialMonth]);

  const load = useCallback(async () => {
    if (!accountId || !year) return;
    setPeriods(null);
    try {
      setPeriods(await getPeriods({ account: accountId, year }));
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    }
  }, [accountId, year, toast]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const entry = periods?.year === selected?.year ? periods.months.find((m) => m.month === selected.month) : null;

  useEffect(() => {
    if (!entry) return;
    setOpening(entry.opening === null || entry.opening === undefined ? '' : String(entry.opening));
    setReason(entry.reason || '');
  }, [entry]);

  useEffect(() => {
    if (current) setLimits({ min: current.minBalance ?? '', max: current.maxBalance ?? '' });
  }, [current]);

  const today = periods?.today;
  const isPast = today && selected && selected.year * 12 + selected.month < today.year * 12 + today.month;
  const hasPrev = entry && entry.calc !== null && entry.calc !== undefined;
  const autoCaja = isCaja && entry?.status === 'automatica';
  const diff = hasPrev && opening !== '' ? Number(opening) - entry.calc : null;
  const showDiff = !isCaja && diff !== null && Math.abs(diff) >= 0.5;

  const step = (delta) => {
    const i = selected.year * 12 + (selected.month - 1) + delta;
    const next = { year: Math.floor(i / 12), month: (i % 12) + 1 };
    setSelected(next);
    setYear(next.year);
  };

  async function save() {
    setSaving(true);
    try {
      if (isCaja) {
        await updateAccountLimits(current.id, { minBalance: toNumber(limits.min), maxBalance: toNumber(limits.max) });
      }
      if (!autoCaja) {
        await savePeriod({ account: current.id, year: selected.year, month: selected.month, openingBalance: Number(opening), differenceReason: reason, confirmPast });
      }
      toast({ status: 'success', title: isCaja ? 'Caja menor actualizada' : 'Caja inicial guardada', duration: 3000 });
      onSaved?.();
      onClose();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e), duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  }

  const bankAccounts = (accounts || []).filter((a) => a.book === current?.book && a.active);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="xl" fontWeight="600">{isCaja ? 'Configurar caja menor' : 'Caja inicial del mes'}</Text>
          <Text fontSize="sm" color="gray.500" fontWeight="400">{isCaja ? 'Flujo de caja · caja inicial y topes' : 'Bancos · el saldo con el que arranca cada cuenta'}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" gap={4}>
          <FormControl>
            <FormLabel htmlFor="op-account">Cuenta</FormLabel>
            <Select id="op-account" value={accountId || ''} onChange={(e) => setAccountId(Number(e.target.value))} isDisabled={isCaja}>
              {bankAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}{a.currency === 'USD' ? ' · USD' : ''}</option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel htmlFor="op-month">Mes</FormLabel>
            <Flex gap={1.5}>
              <IconButton aria-label="Mes anterior" icon={<FiChevronLeft />} variant="outline" onClick={() => step(-1)} />
              <Button id="op-month" flex={1} variant="outline" justifyContent="flex-start" leftIcon={<FiCalendar />} onClick={() => setPickerOpen(!pickerOpen)} fontWeight="400">
                <Text flex={1} textAlign="left">{selected ? monthLabel(selected.year, selected.month) : ''}</Text>
                {entry && <PeriodStatusTag status={entry.status} />}
              </Button>
              <IconButton
                aria-label="Mes siguiente"
                icon={<FiChevronRight />}
                variant="outline"
                isDisabled={today && selected && selected.year * 12 + selected.month >= today.year * 12 + today.month}
                onClick={() => step(1)}
              />
            </Flex>
            {pickerOpen && (
              <Box mt={1.5} border="1px solid" borderColor="gray.200" borderRadius="lg" p={2.5} boxShadow="sm">
                <Flex justify="space-between" align="center" mb={2}>
                  <IconButton aria-label="Año anterior" size="xs" variant="outline" icon={<FiChevronLeft />} onClick={() => setYear(year - 1)} isDisabled={periods?.first && year <= periods.first.year} />
                  <Text fontSize="sm" fontWeight="700">{year}</Text>
                  <IconButton aria-label="Año siguiente" size="xs" variant="outline" icon={<FiChevronRight />} onClick={() => setYear(year + 1)} isDisabled={today && year >= today.year} />
                </Flex>
                {!periods ? (
                  <Flex justify="center" py={3}><Spinner size="sm" /></Flex>
                ) : (
                  <Grid templateColumns="repeat(6, minmax(0, 1fr))" gap={1}>
                    {periods.months.map((m) => {
                      const isSel = selected && m.year === selected.year && m.month === selected.month;
                      return (
                        <Button
                          key={m.month}
                          size="sm"
                          position="relative"
                          title={m.status === 'sin_datos' ? 'Sin datos' : undefined}
                          isDisabled={m.disabled}
                          bg={isSel ? '#574feb' : m.disabled ? 'transparent' : 'gray.50'}
                          color={isSel ? 'white' : undefined}
                          _hover={{ bg: isSel ? '#574feb' : 'gray.100' }}
                          onClick={() => { setSelected({ year: m.year, month: m.month }); setPickerOpen(false); }}
                        >
                          {MONTHS_SHORT[m.month - 1]}
                          {m.status === 'diferencia' && !isSel && <Box position="absolute" top="4px" right="5px" w="6px" h="6px" borderRadius="full" bg="orange.400" />}
                        </Button>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            )}
          </FormControl>

          {!entry ? (
            <Flex justify="center" py={4}><Spinner /></Flex>
          ) : (
            <>
              {entry.status === 'primer_mes' || (!hasPrev && entry.status === 'falta') ? (
                <Box p={3} bg="purple.50" border="1px solid" borderColor="purple.100" borderRadius="lg" fontSize="sm" color="purple.800">
                  Es el primer mes de esta cuenta: no hay un cierre anterior para comparar. Lo que escribas aquí es el punto de partida.
                </Box>
              ) : (
                <Flex justify="space-between" align="center" gap={3} p={3} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg">
                  <Box>
                    <Text fontSize="sm" color="gray.500">Cierre calculado de {MONTHS[(selected.month + 10) % 12]}</Text>
                    <Text fontSize="lg" fontWeight="700" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(entry.calc, current.currency)}</Text>
                  </Box>
                  <Text fontSize="xs" color="gray.500" textAlign="right" maxW="220px">Caja inicial del mes anterior + ingresos − egresos registrados</Text>
                </Flex>
              )}

              {autoCaja ? (
                <FormControl>
                  <FormLabel htmlFor="op-auto">Caja inicial de {MONTHS[selected.month - 1]}</FormLabel>
                  <Input id="op-auto" value={formatMoney(entry.opening)} isDisabled />
                  <FormHelperText>Se toma sola del cierre de {MONTHS[(selected.month + 10) % 12]}. Solo se escribe a mano el primer mes.</FormHelperText>
                </FormControl>
              ) : (
                <FormControl isRequired>
                  <FormLabel htmlFor="op-opening">{isCaja && !hasPrev ? 'Efectivo en caja al empezar' : `Caja inicial de ${MONTHS[selected.month - 1]}`}</FormLabel>
                  <Input id="op-opening" type="number" step={current.currency === 'USD' ? '0.01' : '1'} value={opening} onChange={(e) => setOpening(e.target.value)} isDisabled={!canEdit} sx={{ fontVariantNumeric: 'tabular-nums' }} />
                  {!isCaja && hasPrev && (
                    <FormHelperText>
                      Normalmente sale del extracto.{' '}
                      <Button variant="link" size="sm" colorScheme="purple" onClick={() => setOpening(String(entry.calc))}>Usar el cierre calculado</Button>
                    </FormHelperText>
                  )}
                </FormControl>
              )}

              {showDiff && (
                <Box p={3} bg="orange.50" border="1px solid" borderColor="orange.100" borderRadius="lg">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="sm" fontWeight="600">Diferencia con el cierre calculado</Text>
                    <Text fontSize="md" fontWeight="700" color="orange.600" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(diff, current.currency)}</Text>
                  </Flex>
                  <FormControl isRequired>
                    <FormLabel htmlFor="op-reason" fontSize="sm">Explica la diferencia</FormLabel>
                    <Textarea id="op-reason" rows={2} bg="white" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej.: 4x1000 y comisiones bancarias según el extracto" isDisabled={!canEdit} />
                    <FormHelperText>El superadmin ve esta explicación en el Resumen. Si faltó registrar un movimiento, regístralo y la diferencia baja sola.</FormHelperText>
                  </FormControl>
                </Box>
              )}

              {isCaja && (
                <Box borderTop="1px solid" borderColor="gray.100" pt={4}>
                  <Text fontSize="sm" fontWeight="600" mb={2}>Topes de la caja</Text>
                  <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
                    <FormControl>
                      <FormLabel htmlFor="op-min">Monto mínimo</FormLabel>
                      <Input id="op-min" type="number" value={limits.min} onChange={(e) => setLimits((l) => ({ ...l, min: e.target.value }))} isDisabled={!canEdit} />
                    </FormControl>
                    <FormControl>
                      <FormLabel htmlFor="op-max">Monto máximo</FormLabel>
                      <Input id="op-max" type="number" value={limits.max} onChange={(e) => setLimits((l) => ({ ...l, max: e.target.value }))} isDisabled={!canEdit} />
                    </FormControl>
                  </Grid>
                  <Text fontSize="sm" color="gray.500" mt={2}>Fuera de estos topes, Flujo de caja y el Resumen muestran una alerta.</Text>
                </Box>
              )}

              {isPast && !autoCaja && canEdit && (
                <Box p={3} bg="orange.50" border="1px solid" borderColor="orange.100" borderRadius="lg" fontSize="sm" color="orange.800">
                  <Text mb={2}>
                    Estás cambiando un mes anterior.{' '}
                    {isCaja ? 'Como la caja se arrastra, se recalculan los saldos de todos los meses siguientes.' : `Solo cambia la diferencia de ${MONTHS[selected.month - 1]}; los demás meses no se tocan.`}
                  </Text>
                  <Checkbox isChecked={confirmPast} onChange={(e) => setConfirmPast(e.target.checked)} colorScheme="orange">Confirmo el cambio</Checkbox>
                </Box>
              )}

              <Flex gap={2} align="center" fontSize="sm" color="gray.500">
                <FiClock /> Cada cambio queda en Auditoría con fecha, usuario y el valor anterior.
              </Flex>
            </>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>{canEdit ? 'Cancelar' : 'Cerrar'}</Button>
          {canEdit && (
            <Button
              bg="#574feb"
              color="white"
              _hover={{ bg: '#4a42d6' }}
              onClick={save}
              isLoading={saving}
              isDisabled={!entry || (!autoCaja && opening === '') || (showDiff && !reason.trim()) || (isPast && !autoCaja && !confirmPast)}
            >
              {isPast && !autoCaja ? 'Guardar cambio' : isCaja && autoCaja ? 'Guardar topes' : 'Guardar caja inicial'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
